import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { STORAGE_KEYS, dedupeIds, readStoredIdList } from "@/lib/storage";

interface WatchLaterContextValue {
  watchLater: string[];
  isSaved: (id: string) => boolean;
  toggleWatchLater: (id: string) => void;
  removeWatchLater: (id: string) => void;
  /** 複数IDをまとめて外す（表示できない作品の一括解除など） */
  removeMany: (ids: string[]) => void;
  applyImported: (ids: string[], strategy: "merge" | "replace") => void;
  /** インポート時にストレージへ書き込んだ内容を状態へ反映する */
  hydrate: (ids: string[]) => void;
  count: number;
}

const WatchLaterContext = createContext<WatchLaterContextValue | null>(null);

export const WATCH_LATER_STORAGE_KEY = STORAGE_KEYS.watchLater;

export function WatchLaterProvider({ children }: { children: ReactNode }) {
  const [watchLater, setWatchLater] = useState<string[]>(() =>
    dedupeIds(readStoredIdList(WATCH_LATER_STORAGE_KEY)),
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(WATCH_LATER_STORAGE_KEY, JSON.stringify(watchLater));
    } catch {
      /* 保存できない環境ではセッション内のみ反映（サイト本体は動作を継続） */
    }
  }, [watchLater]);

  const toggleWatchLater = useCallback((id: string) => {
    const value = id.trim();
    if (!value) return;
    // 大文字・小文字が違うIDでも同じ作品として扱う（二重登録を防ぐ）
    const key = value.toLowerCase();
    setWatchLater((current) =>
      current.some((item) => item.toLowerCase() === key)
        ? current.filter((item) => item.toLowerCase() !== key)
        : [value, ...current],
    );
  }, []);

  const removeWatchLater = useCallback((id: string) => {
    const key = id.trim().toLowerCase();
    setWatchLater((current) => current.filter((item) => item.toLowerCase() !== key));
  }, []);

  const removeMany = useCallback((ids: string[]) => {
    const targets = new Set(ids.map((id) => id.toLowerCase()));
    setWatchLater((current) => current.filter((item) => !targets.has(item.toLowerCase())));
  }, []);

  const applyImported = useCallback((ids: string[], strategy: "merge" | "replace") => {
    const incoming = dedupeIds(ids);
    setWatchLater((current) => {
      if (strategy === "replace") return incoming;
      return dedupeIds([...current, ...incoming]);
    });
  }, []);

  const hydrate = useCallback((ids: string[]) => {
    setWatchLater(dedupeIds(ids));
  }, []);

  const value = useMemo<WatchLaterContextValue>(
    () => ({
      watchLater,
      isSaved: (id: string) =>
        watchLater.some((item) => item.toLowerCase() === id.trim().toLowerCase()),
      toggleWatchLater,
      removeWatchLater,
      removeMany,
      applyImported,
      hydrate,
      count: watchLater.length,
    }),
    [watchLater, toggleWatchLater, removeWatchLater, removeMany, applyImported, hydrate],
  );

  return <WatchLaterContext.Provider value={value}>{children}</WatchLaterContext.Provider>;
}

export function useWatchLater(): WatchLaterContextValue {
  const context = useContext(WatchLaterContext);
  if (!context) throw new Error("useWatchLater must be used within WatchLaterProvider");
  return context;
}